import React from 'react';
import { getEmoji, SLACK_COLORS, SLACK_FONTS } from '@/lib/slack-styles';

interface MrkdwnRendererProps {
  text: string;
  className?: string;
}

/**
 * Renders Slack mrkdwn formatted text with proper styling
 * Supports: bold, italic, strikethrough, code, links, and emojis
 */
export function MrkdwnRenderer({ text, className = '' }: MrkdwnRendererProps) {
  const parsed = parseMrkdwn(text);

  return (
    <span
      className={className}
      style={{
        fontFamily: SLACK_FONTS.family,
        fontSize: SLACK_FONTS.sizeBase,
        lineHeight: SLACK_FONTS.lineHeight,
        color: SLACK_COLORS.text,
        wordBreak: 'break-word',
      }}
    >
      {parsed}
    </span>
  );
}

/**
 * Parse mrkdwn text and return React elements
 */
function parseMrkdwn(text: string): React.ReactNode[] {
  const elements: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    // Try to match patterns in order of specificity
    let matched = false;

    // Links: <URL|text> or <URL>
    const linkMatch = remaining.match(/^<([^|>]+)(?:\|([^>]+))?>/) ;
    if (linkMatch) {
      const url = linkMatch[1];
      const linkText = linkMatch[2] || url;
      // Parse the link text for emojis
      const parsedLinkText = parseInlineEmojis(linkText);
      elements.push(
        <a
          key={key++}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: SLACK_COLORS.link,
            textDecoration: 'none',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.textDecoration = 'underline';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.textDecoration = 'none';
          }}
        >
          {parsedLinkText}
        </a>
      );
      remaining = remaining.slice(linkMatch[0].length);
      matched = true;
    }

    // Bold: *text*
    if (!matched) {
      const boldMatch = remaining.match(/^\*([^*\n]+)\*/);
      if (boldMatch) {
        elements.push(
          <strong key={key++} style={{ fontWeight: 700 }}>
            {parseInlineEmojis(boldMatch[1])}
          </strong>
        );
        remaining = remaining.slice(boldMatch[0].length);
        matched = true;
      }
    }

    // Italic: _text_
    if (!matched) {
      const italicMatch = remaining.match(/^_([^_\n]+)_/);
      if (italicMatch) {
        elements.push(
          <em key={key++} style={{ fontStyle: 'italic' }}>
            {parseInlineEmojis(italicMatch[1])}
          </em>
        );
        remaining = remaining.slice(italicMatch[0].length);
        matched = true;
      }
    }

    // Strikethrough: ~text~
    if (!matched) {
      const strikeMatch = remaining.match(/^~([^~\n]+)~/);
      if (strikeMatch) {
        elements.push(
          <s key={key++} style={{ textDecoration: 'line-through' }}>
            {parseInlineEmojis(strikeMatch[1])}
          </s>
        );
        remaining = remaining.slice(strikeMatch[0].length);
        matched = true;
      }
    }

    // Inline code: `code`
    if (!matched) {
      const codeMatch = remaining.match(/^`([^`\n]+)`/);
      if (codeMatch) {
        elements.push(
          <code
            key={key++}
            style={{
              fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
              fontSize: '12px',
              padding: '2px 4px',
              backgroundColor: SLACK_COLORS.codeBackground,
              border: `1px solid ${SLACK_COLORS.codeBorder}`,
              borderRadius: '3px',
              color: '#e01e5a',
            }}
          >
            {codeMatch[1]}
          </code>
        );
        remaining = remaining.slice(codeMatch[0].length);
        matched = true;
      }
    }

    // Emoji: :emoji_name:
    if (!matched) {
      const emojiMatch = remaining.match(/^:([a-z0-9_+-]+):/i);
      if (emojiMatch) {
        const emoji = getEmoji(emojiMatch[1]);
        elements.push(<span key={key++}>{emoji}</span>);
        remaining = remaining.slice(emojiMatch[0].length);
        matched = true;
      }
    }

    // Newline
    if (!matched && remaining.startsWith('\n')) {
      elements.push(<br key={key++} />);
      remaining = remaining.slice(1);
      matched = true;
    }

    // Plain text (consume until next special character or end)
    if (!matched) {
      const plainMatch = remaining.match(/^[^*_~`<:\n]+/);
      if (plainMatch) {
        elements.push(<React.Fragment key={key++}>{plainMatch[0]}</React.Fragment>);
        remaining = remaining.slice(plainMatch[0].length);
        matched = true;
      }
    }

    // If nothing matched, consume one character to avoid infinite loop
    if (!matched) {
      elements.push(<React.Fragment key={key++}>{remaining[0]}</React.Fragment>);
      remaining = remaining.slice(1);
    }
  }

  return elements;
}

/**
 * Parse inline emojis within text (for use inside already-matched patterns)
 */
function parseInlineEmojis(text: string): React.ReactNode[] {
  const elements: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    const emojiMatch = remaining.match(/^:([a-z0-9_+-]+):/i);
    if (emojiMatch) {
      const emoji = getEmoji(emojiMatch[1]);
      elements.push(<span key={key++}>{emoji}</span>);
      remaining = remaining.slice(emojiMatch[0].length);
    } else {
      const plainMatch = remaining.match(/^[^:]+/);
      if (plainMatch) {
        elements.push(<React.Fragment key={key++}>{plainMatch[0]}</React.Fragment>);
        remaining = remaining.slice(plainMatch[0].length);
      } else {
        elements.push(<React.Fragment key={key++}>{remaining[0]}</React.Fragment>);
        remaining = remaining.slice(1);
      }
    }
  }

  return elements;
}

export default MrkdwnRenderer;
